/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useEffect, useState } from "react";
import { Button, Collapse, Dialog } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "cms/auth/hooks/use-auth";
import SimplicityDialogContent from "components/simlplicity-dialog-content/index";
import { ComponentBrowse } from "cms/components/layout/reuse/find-component/dialog/browse/component-browse";
import { DataGridSelector } from "cms/components/layout/reuse/find-component/dialog/data-grid/data-grid-selector";
import { ReuseComponentDialogContext } from "cms/components/layout/reuse/find-component/context/index";
import { buildSample } from "./samples/samples";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    useComponentsWithCategories
} from "cms/models/hooks/use-components-with-categories";

const FindComponentDialog = (props) => {
    
    const {
        handleClose,
        reuseComponent,
        duplicateComponent,
        open
    } = props;
    const { token } = useAuth();
    
    const defaultPageSelectedTitle = "Select a webpage below";
    const [treeVisible, setTreeVisible] = useState(false);
    const [filters, setFilters] = useState([]);
    const [filteredComponents, setFilteredComponents] = useState([]);
    const [pageGuidFilter, setPageGuidFilter] = useState(null);
    const [textSearchFilter, setTextSearchFilter] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);
    const [sortModel, setSortModel] = useState(0);
    const [selectedPageTitle, setSelectedPageTitle] = useState(defaultPageSelectedTitle);
    const [selectedComponent, setSelectedComponent] = useState(null);
    const componentTypesList = useComponentsWithCategories();

    const pageDefaults = {
        page: 0,
        pageSize: 10,
    };

    const filterOperators = [
        { key: "like", label: "contains" },
        { key: "equals", label: "equals" },
        { key: "notNull", label: "not empty" },
        { key: "null", label: "empty" },
        { key: "gt", label: "greater than" },
        { key: "lt", label: "less than" },
    ];
    
    const columnDefinitions = [
        {
            field: "type",
            headerName: "Type",
            width: 150,
            valueFormatter: (params) => {
                const type = params.value;
                const component = componentTypesList.find((item) => {
                    return item.type === type;
                });
                return component?.label || "";
            },
        },
        {
            field: "label",
            headerName: "Label",
            width: 150,
            valueFormatter: (params) => {
                return params.value || "Untitled";
            },
        },
        {
            field: "timesUsed",
            headerName: "Times Used",
            width: 150,
        },
        {
            field: "sample",
            headerName: "Content Sample",
            minWidth: 200,
            flex: 1,
            renderCell: (params) => {
                const { value } = params;
                // return <div dangerouslySetInnerHTML={{ __html: params.value }} />
                return <>{value}</>;
            },
        },
        {
            field: "component",
            headerName: "",
            width: 50,
        },
    ];


    const onReuseComponent = () => {
        const component = structuredClone(selectedComponent);
        let { label } = component;
        if (!label) {
            label = "Untitled Component";
        }
        reuseComponent(component, label);
    };
    
    const onDuplicateComponent = () => {
        const component = structuredClone(selectedComponent);
        let { label } = component;

        if (label) {
            label = `${label} (Copy)`;
        }  else {
            label = "Untitled Component";
        }
        duplicateComponent(component, label);
    };
    
    const selectNode = (nodeGuid) => {
        setPageGuidFilter(nodeGuid);
    };
    
    const hideTree = () => {
        setTreeVisible(false);
    };
    
    useEffect(() => {
        setFilters([]);
    }, []);

    /**
     *  NOTE: API pagination's base index is 1 where MUI Datagrid is 0. As
     *  such, we need to reconcile the current page before we call the API.
     *
     *  NOTE: params for the SDK supports multiple filter and sort keys,  The
     *  params should be formatted  per the following example:
     *
     *  pagination: {
     *     page: 1,
     *     pageSize: 10,
     *   },
     *   filters: [
     *     { key: "pageGuid", value: "assaaas-1212-1212-1212", operator: "=" },
     *   ],
     *   sortModel: [
     *     { key: "label", order: "ASC" },
     *     { key: "content", order: "DESC" },
     *   ],
     * };
     *
     */
    useEffect(() => {
        const setFilters = async () => {
            if (!open) {
                return;
            }

            const pagingDetails = {
                ...pageDefaults,
                page: currentPage,
                pageSize: currentPageSize,
            };

            let applicableFilters = [];
            if (pageGuidFilter) {
                applicableFilters.push({
                    key: "page-guid",
                    value: pageGuidFilter,
                    operator: "=",
                });
            }
            if (textSearchFilter) {
                applicableFilters.push({
                    key: "search-text",
                    value: textSearchFilter,
                    operator: "%",
                });
            }

            const dropdownFilters = filters.map((filter) => {
                return {
                    key: filter.column,
                    value: filter.value,
                    operator: filter.operator,
                };
            });

            applicableFilters = [...applicableFilters, ...dropdownFilters];

            const params = {
                pagination: pagingDetails,
                filters: applicableFilters,
                sorting: sortModel,
            };
            const data = await SimpliCityAdminSDK.component.findReusableComponents(token, params);
            const rows = await Promise.all(data.components.map( async (component) => {
                // the component is a 'row' in the data grid
                // set a row id for the data grid
                component.sample = await buildSample(component, token); //'sample'
                component.id = component.guid;
                return component;
            }));
            setRowCount(data.pagination.total);
            setFilteredComponents(rows);
            setIsLoading(false);
        };
        setFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        currentPage,
        currentPageSize,
        filters,
        open,
        pageGuidFilter,
        sortModel,
        textSearchFilter,
        token
    ]);

    useEffect(() => {
        const findWebpage = async () => {
            if (!pageGuidFilter) {
                setSelectedPageTitle(defaultPageSelectedTitle);
                return false;
            } else {
                const data = await SimpliCityAdminSDK.webpage.findOne(token, pageGuidFilter);
                setSelectedPageTitle(`Viewing components in: <strong>${data.nav_title}</strong>`);
            }
        };
        findWebpage();
        // eslint-disable-next-line
    }, [pageGuidFilter])

    useEffect(() => {
        if (!treeVisible) {
            setPageGuidFilter(null);
        }
    }, [treeVisible]);
    
    const applyTextSearch = (text) => {
        setCurrentPage(1);
        setTextSearchFilter(text);
    };
    const showTree = () => {
        setTreeVisible(true);
    };
    const changePage = (page) => {
        // The data grid starts at zero - simplicity pagination starts at one
        const currentPage = page+1;
        setCurrentPage(currentPage);
    };
    const changePageSize = (size) => {
        setCurrentPageSize(size);
    };
    const applySortModel = (model) => {
        setCurrentPage(1);
        setSortModel(model);
    };

    const createNewFilter = () => {
        return {
            guid: uuidv4(),
            column: columnDefinitions[0].field,
            operator: filterOperators[0].key,
            value: "",
        };
    };
    const addFilter = () => {
        const updatedFilters = [...filters, createNewFilter()];
        setFilters(updatedFilters);
    };

    const clearFilters = () => {
        setFilters([]);
    };

    const removeFilter = (guid) => {
        const updatedFilters = filters.filter((filter) => filter.guid !== guid);
        setFilters(updatedFilters);
    };

    const updateFilter = (updatedFilter) => {
        let updatedFilters = [...filters];
        updatedFilters = updatedFilters.map((filter) => {
            if (filter.guid === updatedFilter.guid) {
                return updatedFilter;
            }
            return filter;
        });
        setFilters(updatedFilters);
    };
    
    const reuseContext = {
        columnDefinitions,
        filterOperators,
        addFilter,
        clearFilters,
        filters,
        removeFilter,
        updateFilter,
    };

    return (
        <ReuseComponentDialogContext.Provider value={reuseContext}>
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth={true}
                maxWidth='lg'
                className={"reuse-component-dialog"}
            >
                <SimplicityDialogContent
                    title={"Find Component"}
                    description={"Search for and select an existing component to reuse or duplicate"}
                    height={"700px"}
                    handleClose={handleClose}
                >
                    <div className='browse-wrapper'>
                        
                        {/* Nav Tree on the left side */}
                        <div>
                            <Collapse orientation="horizontal" in={treeVisible}>
                                <ComponentBrowse
                                    // root={root}
                                    selectedPageTitle={selectedPageTitle}
                                    selectNode={selectNode}
                                    hideTree={hideTree}
                                />
                            </Collapse>
                        </div>
                        
                        {/* Data Grid on the right side */}
                        <div className="data-grid-wrapper">
                            <DataGridSelector
                                currentPageSize={currentPageSize}
                                filteredComponents={filteredComponents}
                                isLoading={isLoading}
                                treeVisible={treeVisible}
                                rowCount={rowCount}
                                setSelectedComponent={setSelectedComponent}
                                applyTextSearch={applyTextSearch}
                                showTree={showTree}
                                changePage={changePage}
                                changePageSize={changePageSize}
                                applySortModel={applySortModel}
                                columnDefinitions={columnDefinitions}
                            />
                        </div>
                    </div>
                    
                    {/* Duplicate and Reuse Buttons at bottom */}
                    <div className='select-component-buttons'>
                        <Button
                            disabled={!selectedComponent}
                            variant='outlined'
                            size='small'
                            color='neutral'
                            startIcon={<ContentCopyIcon />}
                            onClick={onDuplicateComponent}
                        >
                            Duplicate component
                        </Button>
                        <Button
                            className='button-accent-outlined reuse-component-button'
                            size='small'
                            variant='contained'
                            color='accent'
                            disabled={!selectedComponent}
                            onClick={onReuseComponent}
                        >
                            <FontAwesomeIcon icon={"fa-regular fa-recycle"}
                                style={{ marginRight: "8px" }}
                            />
                            Reuse component
                        </Button>
                    </div>
                    
                </SimplicityDialogContent>
            </Dialog>
        </ReuseComponentDialogContext.Provider>
    );
};

export { FindComponentDialog };
